import React, { useState } from 'react';
import { Modal, Button } from 'react-bootstrap';
import { Eye } from 'react-bootstrap-icons';
import { jsPDF } from 'jspdf';
import 'jspdf-autotable';
import { schoolFeeAPI } from '../services/api';
import letterheadImage from '../img/epa_letter_head.jpg';
import '../CSS/FeeDocumentManager.css'

function FeeDocumentManager({
  formatNumber,
  getSchoolFeeByClass,
  showToastMessage,
  onActionReady,
}) {

    const [documentType, setDocumentType] = useState(null);
    const [selectedStudent, setSelectedStudent] = useState(null);
    const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);
    const [documentData, setDocumentData] = useState({
      invoiceNumber: '',
      date: new Date().toLocaleDateString(),
      content: '',
      studentDetails: null,
      signatoryName: '',
      signatoryPosition: ''
    });
    const [paymentHistoryModal, setPaymentHistoryModal] = useState({
      show: false,
      student: null,
      payments: []
    });

    const toDataURL = url => fetch(url)
        .then(response => response.blob())
        .then(blob => new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onloadend = () => resolve(reader.result);
          reader.onerror = reject;
          reader.readAsDataURL(blob);
        }));

        const generateInvoiceNumber = () => {
  const prefix = 'INV';
  const timestamp = new Date().getTime();
  const randomNum = Math.floor(Math.random() * 1000);
  return `${prefix}-${timestamp}-${randomNum}`;
};

const generateReceiptNumber = () => {
  const prefix = 'RCPT';
  const timestamp = new Date().getTime();
  const randomNum = Math.floor(Math.random() * 1000);
  return `${prefix}-${timestamp}-${randomNum}`;
};

const getDefaultContent = (type, student) => {
  const studentName = student?.studentName || '[Student Name]';
  const className = student?.class || '[Class]';
  const fee = formatNumber(getSchoolFeeByClass(student?.class));
  const paid = formatNumber(student?.amount_paid);
  const outstanding = formatNumber(student?.outstanding);

  if (type === 'invoice') {
    return `Dear Parent/Guardian,

This invoice is issued to formally notify you of the school fees payable for ${studentName} (Grade ${className}) for the current academic term. Please find the breakdown of the fee account below.

Total School Fee: K ${fee}
Amount Paid: K ${paid}
Outstanding Balance: K ${outstanding}

We kindly request that the outstanding balance be settled at your earliest convenience to ensure your child's continued and uninterrupted participation in all school programs and activities. Payments can be made in person at the school office during normal business hours, or through any of the payment channels previously communicated to parents and guardians.

If payment has already been made and this invoice was issued in error, or if you believe there is a discrepancy in the amount shown above, please contact the school office as soon as possible so that we can review and correct our records accordingly.

Should you have any questions regarding this invoice, the fee structure, or require a payment arrangement, our administration staff will be happy to assist you.

We sincerely appreciate your prompt attention to this matter and thank you for your continued support and partnership in your child's education.`;
  }

  if (type === 'overdue-statement') {
    return `Dear Parent/Guardian,

This letter serves as a formal statement to notify you that the school fee account for ${studentName} (Grade ${className}) currently reflects an overdue balance. Despite previous communications regarding this account, our records indicate that the balance below remains outstanding as of the date of this statement.

Total School Fee: K ${fee}
Amount Paid: K ${paid}
Outstanding Balance: K ${outstanding}

We understand that families may occasionally face financial difficulties, and we are willing to discuss a suitable payment plan where necessary. However, we must emphasize that continued non-payment may affect your child's ability to fully participate in school activities, access certain school resources, or receive end-of-term academic reports, in line with our school's fee policy.

We kindly request that you settle this outstanding balance in full, or contact the school office within the next few days to arrange a payment plan and avoid further action. Please bring this statement with you, or reference the invoice/account number above, when making payment or discussing your account.

We value your partnership in your child's education and appreciate your urgent attention to resolving this matter.`;
  }

  if (type === 'overdue-reminder') {
    return `Dear Parent/Guardian,

This is a courteous reminder that the school fee balance for ${studentName} (Grade ${className}) remains unpaid, despite our earlier notice regarding this account. We understand that oversights can happen, and we would like to bring this matter back to your attention.

Outstanding Balance: K ${outstanding}

We kindly ask that you settle this balance as soon as possible, or reach out to the school office if you require assistance, clarification, or wish to arrange a payment plan. Prompt settlement of outstanding fees helps the school continue providing quality resources, facilities, and support for all students, including your child.

If payment has already been made recently and this reminder has crossed with your payment, please accept our apologies and disregard this notice — kindly inform the school office so we can update our records.

Thank you for your understanding, cooperation, and continued support as we work together in your child's educational journey.`;
  }

  return '';
};

const handleDocumentAction = (type, student) => {
  setDocumentType(type);
  setSelectedStudent(student);
  
  // Generate initial document data
  const documentNum = type === 'invoice' ? generateInvoiceNumber() : 
                     type === 'receipt' ? generateReceiptNumber() : '';
  
 setDocumentData({
    invoiceNumber: documentNum,
    date: new Date().toLocaleDateString(),
    content: getDefaultContent(type, student),
    studentDetails: student,
    signatoryName: 'Jonah Java',
    signatoryPosition: 'Principal',
    paymentMethod: '',
    amountInWords: ''
  });
};

const handleDocumentContentChange = (e) => {
  setDocumentData({
    ...documentData,
    content: e.target.value
  });
};

const validateDocumentData = () => {
  // Check if date is empty
  if (!documentData.date) {
    return "Please select a date";
  }

  // Special validation for receipts
  if (documentType === 'receipt') {
    // Validate payment method
    if (!documentData.paymentMethod || !documentData.paymentMethod.trim()) {
      return "Please select a payment method";
    }

    // Validate amount in words (letters and spaces only)
    if (!documentData.amountInWords || !documentData.amountInWords.trim()) {
      return "Please enter amount in words";
    }

    const lettersOnlyRegex = /^[A-Za-z\s]+$/;
    if (!lettersOnlyRegex.test(documentData.amountInWords)) {
      return "Amount in words should contain only letters and spaces";
    }
  } 
  // Validation for other document types
  else {
    // Check if content is empty (with null check)
    if (!documentData.content || !documentData.content.trim()) {
      return "Please enter document content";
    }
  }

  // Check if signatoryName is empty (with null check)
  if (!documentData.signatoryName || !documentData.signatoryName.trim()) {
    return "Please enter signatory name";
  }

  // Check if signatoryPosition is empty (with null check)
  if (!documentData.signatoryPosition || !documentData.signatoryPosition.trim()) {
    return "Please enter signatory position";
  }

  return null; // Return null if validation passes
};

const printDocument = () => {
  const error = validateDocumentData();
  if (error) {
    showToastMessage(error, 'danger');
    return;
  }
 // Use the edited values if they exist, otherwise fall back to the original values
 const paidAmount = documentData.paidAmount !== undefined 
 ? documentData.paidAmount 
 : selectedStudent.amount_paid;

  // Determine the document title based on documentType
  let documentTitle;
  if (documentType === 'invoice') {
    documentTitle = 'INVOICE';
  } else if (documentType === 'receipt') {
    documentTitle = 'RECEIPT';
  } else if (documentType === 'overdue-statement') {
    documentTitle = 'OVERDUE STATEMENT';
  } else {
    documentTitle = 'OVERDUE REMINDER';
  }

  let documentHTML = `
    <!DOCTYPE html>
    <html>
      <head>
        <title>${documentTitle} - ${selectedStudent.studentName}</title>
        <style>
          @page { 
            size: A4; 
            margin: 5mm 20mm 20mm 20mm;
          }
          body { 
            font-family: Arial, sans-serif;
            font-size: 12px;
            color: #333;
            line-height: 1.4;
          }
          .letterhead {
            width: 100%;
            height: 40mm;
            object-fit: contain;
            margin-bottom: 5mm;
          }
          .document-title {
            text-align: center;
            font-size: 14px;
            font-weight: bold;
            margin: 5mm 0;
            text-transform: uppercase;
          }
          .student-info {
            display: flex;
            justify-content: space-between;
            margin-bottom: 5mm;
            font-size: 11px;
          }
          .fee-table {
            width: 95%;
            margin: 5mm auto;
            border-collapse: collapse;
            font-size: 11px;
          }
          .fee-table th {
            background-color: #2980b9;
            color: white;
            padding: 3px 5px;
            text-align: left;
            font-weight: bold;
          }
          .fee-table td {
            padding: 3px 5px;
            border-bottom: 1px solid #ddd;
          }
          .notes {
            margin: 5mm 0;
            white-space: pre-wrap;
            font-size: 11px;
          }
          .signature-line {
            width: 50mm;
            border-top: 1px solid #333;
            margin-top: 10mm;
          }
          .signatory-info {
            font-size: 10px;
            margin-top: 2mm;
          }
          /* Receipt-specific styles */
          .receipt-header {
            text-align: center;
            margin-bottom: 10mm;
          }
          .receipt-title {
            font-size: 18px;
            font-weight: bold;
            margin-bottom: 5mm;
          }
          .receipt-number {
            font-size: 14px;
          }
          .divider {
            border-top: 1px solid #333;
            margin: 5mm 0;
          }
          .receipt-details {
            margin-bottom: 10mm;
          }
          .receipt-details div {
            margin-bottom: 3mm;
          }
        </style>
      </head>
      <body>
        <img src="${letterheadImage}" class="letterhead" alt="School Letterhead">`;

  if (documentType === 'receipt') {
    // RECEIPT TEMPLATE
    documentHTML += `
        <div class="receipt-header">
          <div class="receipt-title">RECEIPT</div>
          <div class="receipt-number">No: ${documentData.invoiceNumber}</div>
        </div>
        
        <div class="divider"></div>
        
        <div class="receipt-details">
          <div><strong>Date:</strong> ${documentData.date}</div>
          <div><strong>Received from:</strong> ${selectedStudent.studentName}</div>
          <div><strong>Class:</strong> ${selectedStudent.class}</div>
          <div><strong>Amount Paid:</strong> PGK ${formatNumber(paidAmount)}</div>   

          ${documentData.amountInWords ? `<div><strong>Amount Paid in Words:</strong> ${documentData.amountInWords}</div>` : ''}
          ${documentData.paymentMethod ? `<div><strong>Payment Method:</strong> ${documentData.paymentMethod}</div>` : ''}
          <div><strong>For:</strong> School Fees Payment</div>
        </div>
        
        <div class="divider"></div>
        
        <div>
       
          <div><strong>Outstanding:</strong> PGK ${formatNumber(selectedStudent.outstanding)}</div>
        </div>`;
  } else {
    // INVOICE/OVERDUE DOCUMENT TEMPLATE
    documentHTML += `
        <div class="document-title">${documentTitle}</div>
        
        ${documentType === 'invoice' ? `
          <div class="student-info">
            <div>
              <strong>Invoice Number:</strong> ${documentData.invoiceNumber}<br>
              <strong>Student:</strong> ${selectedStudent.studentName}<br>
              <strong>Class:</strong> ${selectedStudent.class}
            </div>
            <div>
              <strong>Date:</strong> ${documentData.date}
            </div>
          </div>
        ` : `
          <div class="student-info">
            <div>
              <strong>Student:</strong> ${selectedStudent.studentName}<br>
              <strong>Class:</strong> ${selectedStudent.class}
            </div>
            <div>
              <strong>Date:</strong> ${documentData.date}
            </div>
          </div>
        `}
        
        <table class="fee-table">
          <thead>
            <tr>
              <th>Description</th>
              <th>Amount (PGK)</th>
              <th>Paid (PGK)</th>
              <th>Balance (PGK)</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>School Fee</td>
              <td>${formatNumber(getSchoolFeeByClass(selectedStudent.class))}</td>
              <td>${formatNumber(selectedStudent.amount_paid)}</td>
              <td>${formatNumber(selectedStudent.outstanding)}</td>
            </tr>
          </tbody>
        </table>
        
        ${documentData.content ? `
          <div class="notes">${documentData.content.replace(/\n/g, '<br>')}</div>
        ` : ''}`;
  }

  // COMMON FOOTER FOR ALL DOCUMENT TYPES
  documentHTML += `
        <div class="signature-line"></div>
        ${(documentData.signatoryName || documentData.signatoryPosition) ? `
          <div class="signatory-info">
            ${[documentData.signatoryName, documentData.signatoryPosition].filter(Boolean).join(', ')}
          </div>
        ` : ''}
        
        <script>
          setTimeout(() => {
            window.print();
            window.close();
          }, 200);
        </script>
      </body>
    </html>`;

  const printWindow = window.open('', '_blank');
  printWindow.document.write(documentHTML);
  printWindow.document.close();
};

const saveDocument = async () => {
  const error = validateDocumentData();
  if (error) {
    showToastMessage(error, 'danger');
    return;
  }
  
   // Use the edited values if they exist, otherwise fall back to the original values
   const paidAmount = documentData.paidAmount !== undefined 
   ? documentData.paidAmount 
   : selectedStudent.amount_paid;
 
 
  // Determine the document title and filename prefix
  let documentTitle;
  let fileNamePrefix;
  if (documentType === 'invoice') {
    documentTitle = 'INVOICE';
    fileNamePrefix = `Invoice-${documentData.invoiceNumber}`;
  } else if (documentType === 'overdue-statement') {
    documentTitle = 'OVERDUE STATEMENT';
    fileNamePrefix = `Overdue-Statement-${selectedStudent.studentName.replace(/\s+/g, '_')}`;
  } else if (documentType === 'receipt') {
    documentTitle = 'RECEIPT';
    fileNamePrefix = `Receipt-${selectedStudent.studentName.replace(/\s+/g, '_')}-${new Date().toISOString().slice(0, 10)}`;
  } else {
    documentTitle = 'OVERDUE REMINDER';
    fileNamePrefix = `Overdue-Reminder-${selectedStudent.studentName.replace(/\s+/g, '_')}`;
  }
  setIsGeneratingPDF(true);
  
  try {
    const doc = new jsPDF();
    const imgData = await toDataURL(letterheadImage);
    
    if (documentType === 'receipt') {
      // Add receipt content in the template style
      doc.addImage(imgData, 'JPEG', 10, 5, 190, 30);

 
      doc.setFontSize(12);
      doc.text(documentData.invoiceNumber, 105, 50, { align: 'center' });
      
      // Divider line
      doc.setDrawColor(50, 50, 50);
      doc.line(20, 80, 190, 80);
      
      // Receipt details
      doc.setFontSize(12);
      let yPosition = 90;
      
      doc.text(`Date: ${documentData.date}`, 20, yPosition);
      yPosition += 10;
      
      // Divider line
      doc.line(20, yPosition, 190, yPosition);
      yPosition += 15;
      
      doc.text(`Received from: ${selectedStudent.studentName}`, 20, yPosition);
      yPosition += 8;
      doc.text(`Class: ${selectedStudent.class}`, 20, yPosition);
      yPosition += 8;
      doc.text(`Amount Paid: PGK ${formatNumber(paidAmount)}`, 20, yPosition);
      yPosition += 8;
      doc.text(`Amount Paid in Words: ${documentData.amountInWords || 'Not specified'}`, 20, yPosition);
      yPosition += 8;
      doc.text(`Payment Method: ${documentData.paymentMethod || 'Not specified'}`, 20, yPosition);
      yPosition += 8;
      doc.text(`For: School Fees Payment`, 20, yPosition);
      yPosition += 15;
      
      // Divider line
      doc.line(20, yPosition, 190, yPosition);
      yPosition += 15;
   
      doc.text(`Outstanding: PGK ${formatNumber(selectedStudent.outstanding)}`, 20, yPosition);
      yPosition += 20;
      
      // Signature section
      doc.line(120, yPosition, 180, yPosition);
      if (documentData.signatoryName || documentData.signatoryPosition) {
        doc.text(
          [documentData.signatoryName, documentData.signatoryPosition].filter(Boolean).join(', '), 
          150, 
          yPosition + 12, 
          { align: 'center', fontSize: 10 }
        );
      }
    } else {
    // Add letterhead at very top (5mm from top, 190mm width, 30mm height)
    doc.addImage(imgData, 'JPEG', 10, 5, 190, 30);
    
    // Set initial y-position below letterhead
    let yPosition = 40;
    
    // Document title (smaller and closer to letterhead)
    doc.setFontSize(14);
    doc.setTextColor(40, 40, 40);
    doc.text(documentTitle, 105, yPosition, { align: 'center' });
    yPosition += 8;
    
    // Student info (compact layout)
    doc.setFontSize(10);
    
    if (documentType === 'invoice') {
      doc.text(`Invoice Number: ${documentData.invoiceNumber}`, 15, yPosition);
      yPosition += 6;
    }
    
    doc.text(`Student: ${selectedStudent.studentName}`, 15, yPosition);
    doc.text(`Class: ${selectedStudent.class}`, 150, yPosition);
    yPosition += 6;
    doc.text(`Date: ${documentData.date}`, 15, yPosition);
    yPosition += 10;
    
    // Compact fee table
    doc.autoTable({
      startY: yPosition,
      head: [['Description', 'Amount (PGK)', 'Paid (PGK)', 'Balance (PGK)']],
      body: [
        [
          'School Fee', 
          formatNumber(getSchoolFeeByClass(selectedStudent.class)),
          formatNumber(selectedStudent.amount_paid),
          formatNumber(selectedStudent.outstanding)
        ]
      ],
      theme: 'grid',
      headStyles: { 
        fillColor: [41, 128, 185],
        textColor: 255,
        fontStyle: 'bold',
        fontSize: 8
      },
      bodyStyles: {
        fontSize: 8
      },
      columnStyles: {
        0: { cellWidth: 60 },
        1: { cellWidth: 40 },
        2: { cellWidth: 40 },
        3: { cellWidth: 40 }
      },
      margin: { left: 15, right: 15 },
      tableWidth: 180
    });
    
    // Add document content if it exists
    if (documentData.content) {
      yPosition = doc.autoTable.previous.finalY + 8;
      doc.setFont(undefined, 'bold');
      yPosition += 5;
      doc.setFont(undefined, 'normal');
      const splitText = doc.splitTextToSize(documentData.content, 180);
      doc.text(splitText, 15, yPosition);
    }
    
    // Signature section (compact)
    yPosition = Math.max(doc.autoTable.previous.finalY + 15, doc.internal.pageSize.height - 30);
    doc.line(15, yPosition, 60, yPosition);
    
    // Signatory info (small font)
    if (documentData.signatoryName || documentData.signatoryPosition) {
      doc.setFontSize(8);
      const signatoryText = [
        documentData.signatoryName,
        documentData.signatoryPosition
      ].filter(Boolean).join(', ');
      doc.text(signatoryText, 15, yPosition + 5);
    }
  }
    // Save PDF with appropriate filename
    doc.save(`${fileNamePrefix}.pdf`);
  } catch (error) {
    console.error('PDF Generation Error:', error);
    showToastMessage('Failed to generate PDF', 'danger');
  } finally {
    setIsGeneratingPDF(false);
  }
};

 
//  fetchPaymentHistory function
const fetchPaymentHistory = async (studentId, studentName) => {
  try {
    console.log('Fetching payment history for student:', studentId);
    
    const response = await schoolFeeAPI.getPaymentHistory(studentId);
    console.log('API Response:', response);
    
    if (response.data && response.data.success) {
      const formattedPayments = formatPaymentData(response.data.data);
      console.log('Formatted payments:', formattedPayments);
      
      setPaymentHistoryModal({
        show: true,
        student: { id: studentId, name: studentName },
        payments: formattedPayments
      });
    } else {
      console.error('API returned unsuccessful response:', response);
      showToastMessage('Error fetching payment history', 'danger');
    }
  } catch (error) {
    console.error('Error fetching payment history:', error);
    if (error.response) {
      console.error('Error response:', error.response);
    }
    showToastMessage('Error fetching payment history', 'danger');
  }
};

const formatPaymentData = (payments) => {
  const allPayments = [];
  
  // Process school fee payments
  if (payments.schoolFees && payments.schoolFees.length > 0) {
    // Sort by date (oldest first) to calculate individual payments
    const sortedSchoolFees = [...payments.schoolFees].sort((a, b) => 
      new Date(a.payment_date) - new Date(b.payment_date)
    );
    
    // Add each payment as a separate row
    for (let i = 0; i < sortedSchoolFees.length; i++) {
      const fee = sortedSchoolFees[i];
      let individualPayment = parseFloat(fee.amount_paid);
      
      // For subsequent payments, subtract the previous total
      if (i > 0) {
        individualPayment -= parseFloat(sortedSchoolFees[i-1].amount_paid);
      }
      
      allPayments.push({
        type: 'School Fee',
        amount: individualPayment,
        total: i === sortedSchoolFees.length - 1 ? fee.fee_amount : '', // Only show total in latest
        date: fee.payment_date,
        outstanding: i === sortedSchoolFees.length - 1 ? fee.outstanding : '', // Only show outstanding in latest
        isLatest: i === sortedSchoolFees.length - 1
      });
    }
  }
  
  // Process miscellaneous fee payments
  if (payments.miscFees && payments.miscFees.length > 0) {
    // Group misc fees by name
    const feesByName = {};
    
    payments.miscFees.forEach(fee => {
      if (!feesByName[fee.name]) {
        feesByName[fee.name] = [];
      }
      feesByName[fee.name].push(fee);
    });
    
    // Process each fee type
    Object.keys(feesByName).forEach(feeName => {
      // Sort by date (oldest first)
      const sortedFees = feesByName[feeName].sort((a, b) => 
        new Date(a.payment_date) - new Date(b.payment_date)
      );
      
      // Add each payment as a separate row
      for (let i = 0; i < sortedFees.length; i++) {
        const fee = sortedFees[i];
        let individualPayment = parseFloat(fee.paid);
        
        // For subsequent payments, subtract the previous total
        if (i > 0) {
          individualPayment -= parseFloat(sortedFees[i-1].paid);
        }
        
        allPayments.push({
          type: fee.name,
          amount: individualPayment,
          total: i === sortedFees.length - 1 ? fee.amount : '', // Only show total in latest
          date: fee.payment_date,
          outstanding: i === sortedFees.length - 1 ? fee.outstanding : '', // Only show outstanding in latest
          isLatest: i === sortedFees.length - 1
        });
      }
    });
  }
  
  // Sort all payments by date (newest first)
  return allPayments.sort((a, b) => new Date(b.date) - new Date(a.date));
};


// Calculate totals for the footer
const totalAmountPaid = paymentHistoryModal.payments.reduce(
  (sum, payment) => sum + parseFloat(payment.amount || 0), 0
);

const totalFee = paymentHistoryModal.payments
  .filter(payment => payment.isLatest)
  .reduce((sum, payment) => sum + parseFloat(payment.total || 0), 0);

const totalOutstanding = paymentHistoryModal.payments
  .filter(payment => payment.isLatest)
  .reduce((sum, payment) => sum + parseFloat(payment.outstanding || 0), 0);

  React.useEffect(() => {
  if (onActionReady) {
    onActionReady(handleDocumentAction, fetchPaymentHistory);
  }
}, []);

return (
  <>
    {/* ══════════════════════════════════════════
        Document Generation Modal
    ══════════════════════════════════════════ */}
    <Modal show={!!documentType && !!selectedStudent} onHide={() => setDocumentType(null)} size="lg">
      <Modal.Header closeButton className="fdm-modal-header">
        <Modal.Title className="fdm-modal-title">
          {documentType === 'invoice'           && 'Create Invoice'}
          {documentType === 'receipt'           && 'Create Receipt'}
          {documentType === 'overdue-reminder'  && 'Create Overdue Reminder'}
          {documentType === 'overdue-statement' && 'Create Overdue Statement'}
        </Modal.Title>
      </Modal.Header>

      <Modal.Body className="fdm-modal-body">

        {/* Invoice / Receipt number */}
        {(documentType === 'invoice' || documentType === 'receipt') && (
          <div className="fdm-form-group">
            <label className="fdm-label">
              {documentType === 'invoice' ? 'Invoice' : 'Receipt'} Number
            </label>
            <input
              type="text"
              className="fdm-input"
              value={documentData.invoiceNumber}
              readOnly
            />
          </div>
        )}

        {/* Date */}
        <div className="fdm-form-group">
          <label className="fdm-label">Date</label>
          <input
            type="date"
            className="fdm-input"
            value={documentData.date}
            onChange={(e) => setDocumentData({ ...documentData, date: e.target.value })}
          />
        </div>

        {/* Receipt-only fields */}
        {documentType === 'receipt' && (
          <>
            <div className="fdm-form-group">
              <label className="fdm-label">Payment Method</label>
              <select
                className="fdm-input"
                value={documentData.paymentMethod || ''}
                onChange={(e) => setDocumentData({ ...documentData, paymentMethod: e.target.value })}
              >
                <option value="">Select payment method</option>
                <option value="Cash">Cash</option>
                <option value="Bank Transfer">Bank Transfer</option>
                <option value="Cheque">Cheque</option>
                <option value="Other">Other</option>
              </select>
            </div>

            <div className="fdm-form-group">
              <label className="fdm-label">Amount in Words</label>
              <input
                type="text"
                className="fdm-input"
                value={documentData.amountInWords || ''}
                onChange={(e) => setDocumentData({ ...documentData, amountInWords: e.target.value })}
                placeholder="Enter amount in words"
              />
            </div>
          </>
        )}

        {/* Student (read-only) */}
        <div className="fdm-form-group">
          <label className="fdm-label">Student</label>
          <input
            type="text"
            className="fdm-input"
            value={selectedStudent?.studentName || ''}
            readOnly
          />
        </div>

        {/* Class (read-only) */}
        <div className="fdm-form-group">
          <label className="fdm-label">Class</label>
          <input
            type="text"
            className="fdm-input"
            value={selectedStudent?.class || ''}
            readOnly
          />
        </div>

        {/* Document content — not shown for receipts */}
        {documentType !== 'receipt' && (
          <div className="fdm-form-group">
            <label className="fdm-label">Document Content</label>
            <textarea
              className="fdm-input fdm-textarea"
              rows="6"
              value={documentData.content}
              onChange={handleDocumentContentChange}
              placeholder={`Enter ${documentType} content here...`}
            />
          </div>
        )}

        {/* Signatory Name */}
        <div className="fdm-form-group">
          <label className="fdm-label">Signatory Name</label>
          <input
            type="text"
            className="fdm-input"
            value={documentData.signatoryName}
            onChange={(e) => setDocumentData({ ...documentData, signatoryName: e.target.value })}
            placeholder="Enter signatory name"
          />
        </div>

        {/* Signatory Position */}
        <div className="fdm-form-group">
          <label className="fdm-label">Signatory Position</label>
          <input
            type="text"
            className="fdm-input"
            value={documentData.signatoryPosition}
            onChange={(e) => setDocumentData({ ...documentData, signatoryPosition: e.target.value })}
            placeholder="Enter signatory position"
          />
        </div>

        {/* School Fee Summary */}
        <div className="fdm-summary-card">
          <div className="fdm-summary-title">School Fee Summary</div>
          <ul className="fdm-summary-list">
            <li className="fdm-summary-item">
              <span className="fdm-summary-label">School Fee:</span>
              <span className="fdm-summary-value">
                K {formatNumber(getSchoolFeeByClass(selectedStudent?.class))}
              </span>
            </li>
            <li className="fdm-summary-item">
              <span className="fdm-summary-label">Paid:</span>
              <span className="fdm-summary-value">
                {documentType === 'receipt' ? (
                  <input
                    type="number"
                    className="fdm-input fdm-input-inline"
                    value={
                      documentData.paidAmount === undefined
                        ? selectedStudent?.amount_paid ?? ''
                        : documentData.paidAmount
                    }
                    onChange={(e) => {
                      const rawValue = e.target.value;
                      const value = rawValue === '' ? '' : parseFloat(rawValue);
                      const fee = getSchoolFeeByClass(selectedStudent?.class);
                      const outstanding = value === '' ? '' : fee - value;
                      setDocumentData({
                        ...documentData,
                        paidAmount: value,
                        outstandingAmount: outstanding,
                      });
                    }}
                    min="0"
                    max={getSchoolFeeByClass(selectedStudent?.class)}
                    step="0.01"
                  />
                ) : (
                  <span>K {formatNumber(selectedStudent?.amount_paid)}</span>
                )}
              </span>
            </li>
            <li className="fdm-summary-item">
              <span className="fdm-summary-label">Outstanding:</span>
              <span className="fdm-summary-value fdm-outstanding">
                K {formatNumber(selectedStudent?.outstanding)}
              </span>
            </li>
          </ul>
        </div>

      </Modal.Body>

      <Modal.Footer className="fdm-modal-footer">
        <button className="fdm-btn fdm-btn-secondary" onClick={() => setDocumentType(null)}>
          Cancel
        </button>
        <button className="fdm-btn fdm-btn-primary" onClick={saveDocument} disabled={isGeneratingPDF}>
          {isGeneratingPDF ? 'Generating...' : 'Save as PDF'}
        </button>
        <button className="fdm-btn fdm-btn-success" onClick={printDocument}>
          Print
        </button>
      </Modal.Footer>
    </Modal>

    {/* ══════════════════════════════════════════
        Payment History Modal
    ══════════════════════════════════════════ */}
    <Modal
      show={paymentHistoryModal.show}
      onHide={() => setPaymentHistoryModal({ show: false, student: null, payments: [] })}
      size="xl"
    >
      <Modal.Header closeButton className="fdm-modal-header">
        <Modal.Title className="fdm-modal-title">
          <i className="bi bi-clock-history fdm-title-icon" />
          Payment History for {paymentHistoryModal.student?.name}
        </Modal.Title>
      </Modal.Header>

      <Modal.Body className="fdm-modal-body">
        {paymentHistoryModal.payments && paymentHistoryModal.payments.length > 0 ? (
          <>
            <div className="fdm-info-alert">
              <i className="bi bi-info-circle fdm-alert-icon" />
              Showing all payment records for this student
            </div>

            <div className="fdm-table-wrapper">
              <table className="fdm-table">
                <thead className="fdm-table-header">
                  <tr>
                    <th>Date</th>
                    <th>Type</th>
                    <th className="fdm-text-end">Amount Paid (K)</th>
                    <th className="fdm-text-end">Total Fee (K)</th>
                    <th className="fdm-text-end">Outstanding (K)</th>
                  </tr>
                </thead>
                <tbody className="fdm-table-body">
                  {paymentHistoryModal.payments.map((payment, index) => (
                    <tr key={index}>
                      <td>{new Date(payment.date).toLocaleDateString()}</td>
                      <td>
                        <span className={`fdm-type-badge ${
                          payment.type === 'School Fee'
                            ? 'fdm-badge-primary'
                            : 'fdm-badge-success'
                        }`}>
                          {payment.type}
                        </span>
                      </td>
                      <td className="fdm-text-end">{formatNumber(payment.amount)}</td>
                      <td className="fdm-text-end">
                        {payment.total !== '' ? formatNumber(payment.total) : '-'}
                      </td>
                      <td className="fdm-text-end">
                        {payment.outstanding !== '' ? (
                          <span className={
                            payment.outstanding > 0
                              ? 'fdm-outstanding'
                              : 'fdm-paid-full'
                          }>
                            {formatNumber(payment.outstanding)}
                          </span>
                        ) : '-'}
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot className="fdm-table-footer">
                  <tr>
                    <td colSpan="2" className="fdm-footer-label">Totals</td>
                    <td className="fdm-text-end fdm-footer-value">{formatNumber(totalAmountPaid)}</td>
                    <td className="fdm-text-end fdm-footer-value">{formatNumber(totalFee)}</td>
                    <td className="fdm-text-end fdm-footer-value">
                      <span className={totalOutstanding > 0 ? 'fdm-outstanding' : 'fdm-paid-full'}>
                        {formatNumber(totalOutstanding)}
                      </span>
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </>
        ) : (
          <div className="fdm-empty-state">
            <i className="bi bi-receipt-cutoff fdm-empty-icon" />
            <p className="fdm-empty-text">No payment history found for this student.</p>
            <p className="fdm-empty-subtext">
              Student ID: {paymentHistoryModal.student?.id}
            </p>
          </div>
        )}
      </Modal.Body>

      <Modal.Footer className="fdm-modal-footer">
        <button
          className="fdm-btn fdm-btn-primary"
          onClick={() => setPaymentHistoryModal({ show: false, student: null, payments: [] })}
        >
          <i className="bi bi-x-circle fdm-btn-icon" />
          Close
        </button>
      </Modal.Footer>
    </Modal>
  </>
);





}

export default FeeDocumentManager;